import React from 'react';
import { FormControl, Select, InputLabel, MenuItem, Box, Chip } from '@mui/material';

const CustomSelect = ({
    label,
    name,
    value,
    onChange,
    options,
    multiple = false,
    required = false,
    placeholder,
    open,
    onOpen,
    onClose,
    renderOption,
    getOptionLabel = (option) => option.label || option,
    getOptionValue = (option) => option.value || option,
}) => {
    const selectStyles = {
        height: '44px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E5E7EB',
            borderRadius: '8px'
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563EB'
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563EB',
            borderWidth: '1px'
        },
        '& .MuiSelect-select': {
            fontFamily: 'Poppins',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            padding: '12px 16px',
            height: '20px !important',
            minHeight: 'unset !important',
            display: 'flex',
            alignItems: 'center',
            letterSpacing: '0.2px'
        },
        '& .MuiSvgIcon-root': {
            color: '#6B7280',
            right: '12px'
        }
    };

    const menuProps = {
        PaperProps: {
            sx: {
                mt: 0.5,
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                maxHeight: '300px',
                '& .MuiMenuItem-root': {
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    height: '44px',
                    minHeight: 'unset',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease',
                    borderBottom: '1px solid #F3F4F6',
                    letterSpacing: '0.2px',
                    '&:last-child': {
                        borderBottom: 'none'
                    }
                },
                '& .MuiMenuItem-root:hover': {
                    backgroundColor: '#aaaaaa',
                    color: '#fff'
                },
                '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: '#2563EB',
                    color: '#fff',
                    '&:hover': {
                        backgroundColor: '#cfcfcf'
                    }
                },
                '& .MuiList-root': {
                    padding: '0'
                }
            }
        },
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
        },
        disablePortal: false
    };

    const renderValue = (selected) => {
        if (!selected || (Array.isArray(selected) && selected.length === 0)) {
            return (
                <span style={{ 
                    color: '#6B7280', 
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: '0.2px'
                }}>
                    {placeholder || `Select ${label}`}
                </span>
            );
        }

        if (multiple) {
            return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                        // Find the matching option to get the label
                        const option = options.find(opt => getOptionValue(opt) === value);
                        const displayLabel = option ? getOptionLabel(option) : value;
                        
                        return (
                            <Chip
                                key={value}
                                label={displayLabel}
                                sx={{
                                    backgroundColor: '#F3F4F6',
                                    color: '#374151',
                                    fontSize: '13px',
                                    fontFamily: 'Poppins',
                                    borderRadius: '4px',
                                    height: '24px',
                                    '& .MuiChip-label': {
                                        padding: '0 8px'
                                    },
                                    '& .MuiChip-deleteIcon': {
                                        color: '#6B7280',
                                        fontSize: '16px',
                                        marginRight: '4px',
                                        '&:hover': {
                                            color: '#374151'
                                        }
                                    }
                                }}
                                onDelete={() => {
                                    const newValue = selected.filter(item => item !== value);
                                    onChange({ target: { name, value: newValue } });
                                }}
                                onMouseDown={(event) => event.stopPropagation()}
                            />
                        );
                    })}
                </Box>
            );
        }

        // For single select, find the option and get its label
        const option = options.find(opt => getOptionValue(opt) === selected);
        return option ? getOptionLabel(option) : selected;
    };

    return (
        <FormControl fullWidth variant="outlined" sx={{ 
            '& .MuiInputLabel-root': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontWeight: 500,
                color: '#6B7280',
                letterSpacing: '0.2px',
                '&.MuiInputLabel-shrink': {
                    padding: '0 8px',
                    color: '#6B7280',
                    fontSize: '14px',
                }
            }
        }}>
            {label && <InputLabel>{label}{required ? ' *' : ''}</InputLabel>}
            <Select
                name={name}
                value={value}
                onChange={onChange}
                multiple={multiple}
                label={label ? `${label}${required ? ' *' : ''}` : undefined}
                required={required}
                open={open}
                onOpen={onOpen}
                onClose={onClose}
                renderValue={renderValue}
                sx={selectStyles}
                MenuProps={menuProps}
            >
                {options.map((option) => (
                    <MenuItem 
                        key={getOptionValue(option)} 
                        value={getOptionValue(option)}
                    >
                        {renderOption ? renderOption(option) : getOptionLabel(option)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default CustomSelect;